import { assertType, CrDiff, uuid } from '@/model';
import { GLOBAL_DB, initIndexDB, KvDatabase, KvStore } from './Database';
import { RepoCollection } from './RepoCollection';

class Workspace {
    private _uuid:string;
    private _name:string;
    private _type:string | undefined;
    private _repoCollection: RepoCollection | undefined = undefined;
    private _db:KvDatabase|undefined = undefined;

    constructor(json:WorkspaceJson) {
        assertType(json.uuid, "uuid", String);
        assertType(json.name, "name", String);
        if (json.type) {
            assertType(json.type, "type", String);
        }
        this._uuid = json.uuid;
        this._name = json.name;
        this._type = json.type;
    }

    get name() { return this._name; }
    get uuid() { return this._uuid; }
    get type() { return this._type; }

    toJson() : WorkspaceJson {
        return {
            uuid: this._uuid,
            name: this._name,
            type: this._type,
        }
    }

    async getRepoCollection() {
        if (this._repoCollection) {
            return this._repoCollection;
        }
        if (!this._db) {
            this._db = new KvDatabase(this._uuid);
            await this._db.init();
        }
        this._repoCollection =  new RepoCollection(this._db);
        await this._repoCollection.init();
        return this._repoCollection;
    }

    clear() {
        this._repoCollection = undefined;
    }

    delete() {
        this.clear();
        window.indexedDB.deleteDatabase(this._uuid);
    }

    async downloadContent() : Promise<WorkspaceContentJson> {
        const json:Record<string,CrDiff<any>[]> = {};
        if (!this._db) {
            this._db = new KvDatabase(this._uuid);
            await this._db.init();
        }
        
        const names = this._db.getStoreNames();
        for (let i = 0; i < names.length; i++) {
            const name = names[i];
            const store:KvStore<any> = this._db.getStoreByName(name);
            const objects = await store.getAll();
            json[name] = objects;
        }
    
        return {
            workspace : this.toJson(),
            content : json
        };
    }

    static async fromContentJson(workspaceContent: WorkspaceContentJson) {
        const wjson = workspaceContent.workspace;
        const json = workspaceContent.content;

        wjson.uuid = uuid();
        wjson.dbname = uuid();
        const workspace = new Workspace(wjson);
        workspace._db = new KvDatabase(workspace.uuid);
        await workspace._db.init();

        const names = Object.keys(json);
        for (let i = 0; i < names.length; i++) {
            const name = names[i];
            const store:KvStore<any> =workspace._db.getStoreByName(name);
            const list = json[name];
            for (let j = 0; j < list.length; j++) {
                const item = list[j];
                await store.write(item.uuid, item);
            }
        }
        return workspace;
    }
}

interface WorkspaceContentJson {
    workspace: WorkspaceJson;
    content:Record<string,CrDiff<any>[]>;
}

interface WorkspaceJson {
    uuid: string;
    name: string;
    type?:string;
    dbname?: string;
}

interface Dictionary<V> {
    get(key:string) : Promise<V | undefined>,
    put(key:string, value: V) : Promise<void>
    getAll(): Promise<V[]>
    delete(key:string): Promise<void> 
}

class NoOpDictionary<T> implements Dictionary<T> {
    get(key: string): Promise<T | undefined> {
        throw new Error('Method not implemented.');
    }
    put(key: string, value: T): Promise<void> {
        throw new Error('Method not implemented.');
    }
    getAll(): Promise<T[]> {
        throw new Error('Method not implemented.');
    }
    delete(key: string): Promise<void> {
        throw new Error('Method not implemented.');
    }
}

class IndexDBStorageDictionary<V> implements Dictionary<V> {
    private _db:IDBDatabase;
    private _storeName:string;
    private _keyName:string;

    constructor(db:IDBDatabase, storeName:string, keyName: string) {
        this._db = db;
        this._storeName = storeName;
        this._keyName = keyName;
    }

    async get(key: string) : Promise<V | undefined> {
        const tx = this._db.transaction(this._storeName, 'readonly');
        const store = tx.objectStore(this._storeName);

        const promised = new Promise<ValueContainer<V>>((resolve, reject) => {
            const request = store.get(key);
            request.onsuccess = event => resolve(request.result);
            request.onerror = event => reject(new Error(`Get failed for key ${key}`));
        });
        const value = await promised;
        return value ? value.value : undefined;
    }

    async put(key: string, value: V): Promise<void> {
        const tx = this._db.transaction(this._storeName, 'readwrite');
        const store = tx.objectStore(this._storeName);

        await new Promise<IDBValidKey>((resolve, reject) => {
            const obj:Record<any,any> = {};
            obj[this._keyName] = key;
            obj["value"] = value;
            const request = store.put(obj);
            request.onsuccess = event => resolve(request.result);
            request.onerror = event => reject(new Error(`Write failed for key ${key}`));
        });
    }

    async getAll(): Promise<V[]> {
        const tx = this._db.transaction(this._storeName, 'readonly');
        const store = tx.objectStore(this._storeName);

        const promised = new Promise<ValueContainer<V>[]>((resolve, reject) => {
            const request:IDBRequest = store.getAll();
            request.onsuccess = event => resolve(request.result);
            request.onerror = event => reject(new Error(`Getall failed for store ${this._storeName}`));
        })
        .then(vcs => vcs.map(vc => vc.value));
        return promised;    
    }
    
    async delete(key: string): Promise<void> {
        const tx = this._db.transaction(this._storeName, 'readwrite');
        const store = tx.objectStore(this._storeName);

        await new Promise<IDBValidKey>((resolve, reject) => {
            const request = store.delete(key);
            request.onsuccess = event => resolve(request.result);
            request.onerror = event => reject(new Error(`Write failed for key ${key}`));
        });
    }
}

interface ValueContainer<V> {
    value: V
}

class Workspaces {
    private _spaces: Record<string, Workspace> = {};
    private _current: Workspace | undefined = undefined;
    private _workspaceStore: Dictionary<WorkspaceJson> = new NoOpDictionary();
    private _generalStore: Dictionary<string> = new NoOpDictionary();
    private _init = false;

    private WORKSPACE_DB = "Global";

    private static instance = new Workspaces();

    static getInstance() {
        return Workspaces.instance;
    }

    private async init() {
        if (this._init) return;
        
        const indexDB = await initIndexDB(this.WORKSPACE_DB)

        this._workspaceStore = new IndexDBStorageDictionary<WorkspaceJson>(indexDB, GLOBAL_DB.WORKSPACE_STORE_NAME, "uuid");
        this._generalStore =  new IndexDBStorageDictionary(indexDB, GLOBAL_DB.SETTING_STORE_NAME, "key");

        const spaces = await this._workspaceStore.getAll();

        spaces.forEach(space => {
            this._spaces[space.uuid] = new Workspace(space);
        });
        this._init = true;
    }

    async getAll() {
        await this.init();
        return Object.values(this._spaces);
    }

    async getCurrent() : Promise<Workspace | undefined> {
        await this.init();
        if (!this._current) {
            const uuid = await this._generalStore.get("currentWorkspace");
            this._current = uuid ? this._spaces[uuid] : undefined;
        }
        return this._current;
    }

    async setCurrent(uuid: string) {
        const workspace = this._spaces[uuid];
        if (!workspace) {
            throw new Error("Illegal state");
        }
        await this._generalStore.put("currentWorkspace", workspace.uuid);
        this._current = workspace;
    }

    async clearCurrent() {
        await this._generalStore.put("currentWorkspace", "");
        this._current = undefined;
    }

    async createNew(json: WorkspaceJson) : Promise<Workspace> {
        await this.init();
        if (this._spaces[json.uuid]) {
            return Promise.reject(`Workspace '${name}' already exists!`);
        }
        const ws = new Workspace(json);
        this._spaces[json.uuid] = ws;
        await this.saveWorkSpace(ws);
        return ws;
    }

    private async saveWorkSpace(ws: Workspace) {
        const wsJson = ws.toJson();
        await this._workspaceStore.put(ws.uuid, wsJson);
    }

    private async deleteWorkSpace(ws: Workspace) {
        await this._workspaceStore.delete(ws.uuid);
    }

    async importFromJson(json: WorkspaceContentJson) : Promise<Workspace> {
        const ws = await Workspace.fromContentJson(json);
        this._spaces[ws.uuid] = ws;
        await this.saveWorkSpace(ws);
        return ws;
    }

    async delete(workspace:Workspace) {
        if (workspace.uuid in this._spaces) {
            delete this._spaces[workspace.uuid];
            workspace.delete();
            await this.deleteWorkSpace(workspace);
        }
    }

    async rename(json:WorkspaceJson) {
        if ((json.uuid in this._spaces) && (json.name)) {
            const ws = new Workspace(json);
            this._spaces[json.uuid] = ws;
            await this.saveWorkSpace(ws);
        }
    }
}


function *generateUuid(nb:Number) {
    for (let n = 0; n < nb; n++) {
        yield uuid();
    }
}

declare global {
    interface Window { 
        wss: any, 
        kv:any,
        generateUuid:any,
        testTemplate: any,
    }
}
window.wss = Workspaces.getInstance();
window.generateUuid = generateUuid;

export { Workspaces, Workspace, WorkspaceJson, WorkspaceContentJson }
